import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Check,
  Plus,
  ExternalLink,
  Users,
  Calendar,
  ArrowLeft
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

export default function AdminTrialCodes() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form state for new code
  const [newCode, setNewCode] = useState("");
  const [maxUses, setMaxUses] = useState(100);
  const [durationDays, setDurationDays] = useState(7);
  const [expiresInDays, setExpiresInDays] = useState(365);
  const [creating, setCreating] = useState(false);

  const baseUrl = "https://compass.truenorth-zone.com";

  const fetchCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/trial-codes`, {
        withCredentials: true
      });
      setCodes(response.data.codes || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load trial codes");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreateCode = async () => {
    if (!newCode.trim()) {
      toast.error("Please enter a code");
      return;
    }

    setCreating(true);
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);

      await axios.post(
        `${API}/admin/trial-codes`,
        {
          code: newCode.toUpperCase(),
          trial_duration_days: durationDays,
          max_uses: maxUses,
          expires_at: expiresAt.toISOString()
        },
        { withCredentials: true }
      );

      toast.success("Trial code created!");
      setShowCreateDialog(false);
      setNewCode("");
      setMaxUses(100);
      setDurationDays(7);
      setExpiresInDays(365);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (code, currentActive) => {
    try {
      await axios.put(
        `${API}/admin/trial-codes/${code}`,
        { is_active: !currentActive },
        { withCredentials: true }
      );
      toast.success(`Code ${!currentActive ? "activated" : "deactivated"}`);
      fetchCodes();
    } catch (error) {
      toast.error("Failed to update code");
    }
  };

  const copyToClipboard = async (code) => {
    const url = `${baseUrl}/trial?code=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      toast.success("Link copied!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiry";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#a69373]"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <section className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#a69373' }}>Trial Codes</h1>
          <p className="text-white/70">Manage shareable trial links for WhatsApp distribution</p>
        </section>

        {/* Actions */}
        <section className="mb-8 flex justify-end">
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#a69373] hover:bg-[#8b7a5e] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Code
          </Button>
        </section>

        {/* Stats Summary */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#a69373]/20 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#a69373]" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Total Codes</p>
                  <p className="text-xl font-bold text-white">{codes.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Active Codes</p>
                  <p className="text-xl font-bold text-white">
                    {codes.filter(c => c.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Total Redemptions</p>
                  <p className="text-xl font-bold text-white">
                    {codes.reduce((sum, c) => sum + (c.use_count || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Codes List */}
        <section className="space-y-4">
        {codes.map((code) => (
          <Card key={code.code} className={`glass-card ${!code.is_active ? "opacity-60" : ""}`}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-lg font-bold text-white">
                      {code.code}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      code.is_active 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-white/10 text-white/50"
                    }`}>
                      {code.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-white/60">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {code.use_count || 0} / {code.max_uses} uses
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {code.trial_duration_days} day trial
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Expires: {formatDate(code.expires_at)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="text-xs bg-white px-2 py-1 rounded text-[#1a1a1a] flex-1 truncate">
                      {baseUrl}/trial?code={code.code}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(code.code)}
                      className="shrink-0 text-white hover:bg-white/10"
                    >
                      {copiedCode === code.code ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`active-${code.code}`} className="text-sm text-white">
                      Active
                    </Label>
                    <Switch
                      id={`active-${code.code}`}
                      checked={code.is_active}
                      onCheckedChange={() => handleToggleActive(code.code, code.is_active)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {codes.length === 0 && (
          <Card className="glass-card">
            <CardContent className="p-8 text-center">
              <Gift className="w-12 h-12 text-[#a69373]/40 mx-auto mb-4" />
              <p className="text-white/60">No trial codes yet</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                variant="link"
                className="text-[#a69373]"
              >
                Create your first code
              </Button>
            </CardContent>
          </Card>
        )}
        </section>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md bg-[#f5f9f5] border-[#c5d5c5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Create Trial Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-[#1a1a1a]">Code</Label>
              <Input
                id="code"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="e.g., SUMMER2026"
                className="font-mono bg-white border-[#c5d5c5] text-[#1a1a1a]"
              />
              <p className="text-xs text-[#1a1a1a]/50">
                Will be converted to uppercase. Use URL-safe characters.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="maxUses" className="text-[#1a1a1a]">Max Uses</Label>
                <Input
                  id="maxUses"
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value) || 100)}
                  min={1}
                  className="bg-white border-[#c5d5c5] text-[#1a1a1a]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration" className="text-[#1a1a1a]">Trial Days</Label>
                <Input
                  id="duration"
                  type="number"
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value) || 7)}
                  min={1}
                  max={30}
                  className="bg-white border-[#c5d5c5] text-[#1a1a1a]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expires" className="text-[#1a1a1a]">Code Valid For (Days)</Label>
              <Input
                id="expires"
                type="number"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 365)}
                min={1}
                className="bg-white border-[#c5d5c5] text-[#1a1a1a]"
              />
              <p className="text-xs text-[#1a1a1a]/50">
                How long the code can be redeemed (not the trial duration)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-[#c5d5c5] text-[#1a1a1a]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCode}
              disabled={creating || !newCode.trim()}
              className="bg-[#a69373] hover:bg-[#8b7a5e] text-white"
            >
              {creating ? "Creating..." : "Create Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
}
